import { uuidv4 } from '../../_Global/js/utils';

type AccordionExpandBehaviour = "single" | "multiple";

function Accordion(component: HTMLFormElement): void {
  const accordionActiveClass = 'accordion--active';
  const accordionIconAnimationClass = 'icon__expand--active';
  const accordionButtons = component.querySelectorAll('[data-role="accordion-toggle-button"]');
  const accordionToggleAllButton = component.querySelector('[data-role="accordion-toggle-all-button"]');
  const accordionToggleAllLabel = component.querySelector('[data-role="accordion-toggle-all-action"]');
  const expandBehaviour = component.getAttribute('data-expand-behaviour') as AccordionExpandBehaviour || "single";

  let hasExpandedAll = false;

  /**
   * Respond to clicks on an accordion button
   */
  function onAccordionButtonClick(evt: Event) {
    const accordionButton = evt.currentTarget as Element;
    const isExpanded = accordionButton.classList.contains(accordionActiveClass);

    evt.preventDefault();

    if (isExpanded) {
      // Close the accordion
      accordionButton.classList.remove(accordionActiveClass, accordionIconAnimationClass);
      accordionButton.setAttribute('aria-expanded', 'false');
    } else {
      // Check if more than one accordion can be open
      if (expandBehaviour === 'single') {
        // Close all accordions
        toggleAllAccordions(false);
      }

      // Open the current accordion
      accordionButton.classList.add(accordionActiveClass, accordionIconAnimationClass);
      accordionButton.setAttribute('aria-expanded', 'true');
    }

    checkAccordionToggleStatus();
  }

  /**
   * Respond to clicks on the toggle (open/close)
   * all accordions button
   */
  function onToggleAllClick(evt: Event) {
    evt.preventDefault();

    if (hasExpandedAll) {
      // Close all accordions
      toggleAllAccordions(false);
    } else {
      // Open all accordions
      toggleAllAccordions(true);
    }
  }

  /**
   * Set all accordions in this component as
   * either open or closed.
   *
   * @param {Boolean} setAsOpen
   * The state to set accordions to.
   */
  function toggleAllAccordions(setAsOpen: Boolean) {
    if (setAsOpen) {
      accordionButtons.forEach((button) => {
        button.classList.add(accordionActiveClass, accordionIconAnimationClass);
        button.setAttribute('aria-expanded', 'true');
      });

      hasExpandedAll = true;

      accordionToggleAllButton?.classList.add(accordionIconAnimationClass);

      if (accordionToggleAllLabel) {
        accordionToggleAllLabel.textContent = 'Collapse';
      }
    } else {
      accordionButtons.forEach((button) => {
        button.classList.remove(accordionActiveClass, accordionIconAnimationClass);
        button.setAttribute('aria-expanded', 'false');
      });

      hasExpandedAll = false;

      accordionToggleAllButton?.classList.remove(accordionIconAnimationClass);

      if (accordionToggleAllLabel) {
        accordionToggleAllLabel.textContent = 'Expand';
      }
    }
  }

  /**
   * Generate and assign IDs and aria attributes to the
   * accordion buttons and their associated content.
   */
  function assignAccordionAttributesAndListeners() {
    accordionButtons.forEach((button, index) => {
      const accordionId = uuidv4();
      const buttonId = `acc-${accordionId}-button-${index}`;
      const contentId = `acc-${accordionId}-section-${index}`;
      const accordionContent = button.nextElementSibling;

      // Set the button ID
      button.setAttribute('id', buttonId);

      // Link to the content
      button.setAttribute('aria-controls', contentId);

      button.addEventListener('click', onAccordionButtonClick);

      // Set the content ID
      accordionContent?.setAttribute('id', contentId);

      // Link to the button
      accordionContent?.setAttribute('aria-labelledby', buttonId);
    });
  }

  /**
   * Check to see if all accordions have been expanded
   * or collapsed.
   * This is used to determine the state of the optional
   * expand/collapse all button.
   */
  function checkAccordionToggleStatus() {
    let accordionActiveCount = 0;

    accordionButtons.forEach((button) => {
      if (button.classList.contains(accordionActiveClass)) {
        accordionActiveCount++;
      }
    });

    if (accordionActiveCount === 0) {
      // No accordions are open
      toggleAllAccordions(false);
    }

    if (accordionActiveCount === accordionButtons.length) {
      // All accordions are open
      toggleAllAccordions(true);
    }
  }

  assignAccordionAttributesAndListeners();

  checkAccordionToggleStatus();

  // Add functionality for the optional toggle/collapse all button
  if (accordionToggleAllButton) {
    accordionToggleAllButton.addEventListener('click', onToggleAllClick);
  }
};

export default Accordion;
